let activeInsurancePropertyTable = null

/** The property page owns this lifecycle, just like its other injected sections. */
function injectInsurancePropertyTable({
  propertyId,
  propertyHrId,
  buildings: initialBuildings = [],
  buildingsLoaded: initialBuildingsLoaded = initialBuildings.length > 0,
  // Lets the section and its loader appear before the buildings load; resolves to { buildings, buildingsLoaded }.
  buildingsRequest = null,
}) {
  const isCurrentPage = () =>
    window.location.pathname === `/properties/${propertyHrId}/edit`
  if (!isCurrentPage()) return { disconnect() {} }
  activeInsurancePropertyTable?.disconnect()
  // Filled in place so the row actions keep seeing the loaded buildings.
  const buildings = [...initialBuildings]
  let buildingsLoaded = initialBuildingsLoaded
  let buildingsReady = !buildingsRequest
  let root = null
  let section = null
  // Only set while the overview owns a standalone section (no native one on the page).
  let sidebarLink = null
  let standaloneNumber = 0
  let rows = []
  // From the API; null keeps the full catalog in the Sparte filter.
  let allowedKinds = null
  let loaded = false
  // The overview waits for the first load (or its failure/timeout) so it opens with its rows.
  let settled = false
  let rendered = false
  let disposed = false
  let refreshRequest = null
  let timer = null
  let pending = true
  let pendingLoader = null
  let scrolledToHash = false
  let page = 1
  // Column sort chosen by clicking a header; null keeps the status order.
  let sort = null
  let lastFilterKey = ''
  // Set when a Lux page opens in this tab; the overview stays visible until the browser replaces the page.
  let leaving = false
  let leavingHost = null
  let leavingTimer = null
  const pendingTimer = setTimeout(settle, LUX_INSURANCE_PENDING_TIMEOUT_MS)
  const apiUrl = `${baseApiUrl}/api/insurances/properties/${encodeURIComponent(propertyId)}`
  const blockedPopover = createInsuranceDeleteBlockedPopover(() => root)
  const actions = createInsuranceRowActions({
    apiUrl,
    buildings,
    request: luxInsuranceRequest,
    isDisposed: () => disposed,
    isCurrentPage,
    getRoot: () => root,
    getRows: () => rows,
    setRows: (next) => {
      rows = next
    },
    renderRows,
    refresh,
    abortRefresh: () => refreshRequest?.abort(),
    showDeleteBlocked: blockedPopover.show,
  })

  // Sits directly above the hidden native section, which Impower's React still owns and must not lose.
  // A native section that still shows other content keeps it, and the overview goes inside instead.
  function ensureSection() {
    const native = findInsuranceFormSection()
    if (!native) return ensureStandaloneSection()
    if (sidebarLink) {
      removeSection()
      root = null
    }
    if (!isNativeInsuranceSectionReplaceable(native)) {
      if (section) {
        removeSection()
        root = null
      }
      return native.querySelector('.section-content')
    }
    if (root && !section?.contains(root)) {
      root.remove()
      root = null
    }
    section ??= createInsuranceFormSection(
      native
        .querySelector('.main-section-header .section-number')
        ?.textContent.trim() || '8'
    )
    if (native.previousElementSibling !== section) native.before(section)
    return section.querySelector('.section-content')
  }

  function ensureStandaloneSection() {
    if (!lastNumberedPropertySection()) return null
    if (section && !sidebarLink) {
      removeSection()
      root = null
    }
    if (!section?.isConnected) {
      // A dropped section is re-placed with its overview, so the rows need no refetch.
      // Only runs on this property's page, so the gap it left is still here.
      const dropped = section
      removeSection(true)
      placeStandaloneSection(dropped)
    } else if (!sidebarLink.isConnected) {
      insertInsuranceSidebarLink(
        sidebarLink,
        standaloneNumber,
        luxSectionAfterInsurance()?.section.id
      )
    }
    return section.querySelector('.section-content')
  }

  function placeStandaloneSection(dropped) {
    const next = luxSectionAfterInsurance()
    const previous = next ? null : lastNumberedPropertySection()
    standaloneNumber = next?.number ?? previous.number + 1
    // Lux entries numbered from here on (Zählerstände, Benachrichtigungseinstellungen) move down one.
    shiftPropertySectionNumbers(standaloneNumber, 1)
    section = dropped ?? createInsuranceFormSection(String(standaloneNumber))
    section.querySelector('.section-number').textContent =
      String(standaloneNumber)
    if (next) next.section.before(section)
    else previous.section.after(section)
    sidebarLink = createInsuranceSidebarLink(String(standaloneNumber), section)
    insertInsuranceSidebarLink(sidebarLink, standaloneNumber, next?.section.id)
  }

  // Only the page the section still sits on has the gap; after a navigation the detached nodes must not renumber the next page.
  function removeSection(closeGap = Boolean(section?.isConnected)) {
    if (sidebarLink && closeGap) {
      shiftPropertySectionNumbers(standaloneNumber + 1, -1)
    }
    section?.remove()
    section = null
    sidebarLink?.remove()
    sidebarLink = null
  }

  function markPending() {
    if (!pending || disposed || !isCurrentPage()) return
    ensureInsurancePendingStyles()
    const content = ensureSection()
    if (!content || pendingLoader?.parentElement === content) return
    pendingLoader?.remove()
    pendingLoader = createInsurancePendingLoader(propertyHrId, buildings)
    // The skeleton already carries the live create button.
    pendingLoader.addEventListener('click', (event) => {
      if (isSameTabLuxLink(event)) showLeaving(pendingLoader)
    })
    content.prepend(pendingLoader)
  }

  function clearPending() {
    pending = false
    clearTimeout(pendingTimer)
    const wasLeavingHost = leaving && leavingHost === pendingLoader
    pendingLoader?.remove()
    pendingLoader = null
    // The overview settled while the create page opens from the skeleton.
    if (wasLeavingHost) showLeaving()
  }

  function settle() {
    settled = true
    mount()
    if (buildingsReady) clearPending()
  }

  async function loadBuildings() {
    try {
      const result = await buildingsRequest
      if (disposed) return
      buildings.push(...(result?.buildings ?? []))
      buildingsLoaded = Boolean(result?.buildingsLoaded)
    } catch (_) {
      // Without buildings the overview still lists its rows; only the building filter stays off.
    }
    buildingsReady = true
    if (disposed || !settled) return
    mount()
    clearPending()
  }

  // The Lux create page returns here and leaves its success message for the overview.
  function revealAfterReturn() {
    if (scrolledToHash || window.location.hash !== LUX_INSURANCE_SECTION_HASH) {
      return
    }
    scrolledToHash = true
    root.scrollIntoView({ block: 'start' })
    let notice = null
    try {
      notice = sessionStorage.getItem(LUX_INSURANCE_NOTICE_KEY)
      sessionStorage.removeItem(LUX_INSURANCE_NOTICE_KEY)
    } catch (_) {
      // Blocked storage only loses the one-time notice.
    }
    if (notice) showToast(notice, 'success')
  }

  function renderRows() {
    const search = root
      .querySelector('[data-insurance-search]')
      .value.trim()
      .toLocaleLowerCase('de')
    const building = root.querySelector('[data-insurance-building]').value
    const kind = root.querySelector('[data-insurance-kind]').value
    const filtered = filterInsuranceRows(rows, buildings, {
      search,
      building,
      kind,
    })
    const filterKey = `${search}|${building}|${kind}`
    if (filterKey !== lastFilterKey) page = 1
    lastFilterKey = filterKey
    blockedPopover.close()
    const count = root.querySelector('[data-insurance-total-count]')
    if (count) {
      count.textContent = `(${insuranceCount(rows.length, 'Eintrag', 'Einträge')})`
    }
    const table = renderInsuranceTable(filtered, propertyHrId, buildings, {
      all: rows,
      buildingId: building,
      buildingsLoaded,
      page,
      sort,
    })
    root.querySelector('[data-insurance-table]').innerHTML =
      (search || building || kind) && !filtered.length
        ? table.replace(
            /(<td [^>]*data-insurance-empty>)[^<]*/,
            '$1Keine Policen für diese Auswahl gefunden.'
          )
        : table
  }

  function applyData(data) {
    if (!Array.isArray(data?.insurances)) {
      throw new Error('Versicherungen konnten nicht geladen werden.')
    }
    rows = data.insurances
    allowedKinds = Array.isArray(data.kinds) ? data.kinds : null
    loaded = true
  }

  function showRows() {
    rendered = true
    root.querySelector('[data-insurance-search]').disabled = false
    root.querySelector('[data-insurance-building]').disabled = !buildingsLoaded
    const kindFilter = root.querySelector('[data-insurance-kind]')
    const kinds = insuranceFilterKinds(allowedKinds, rows)
    const selected = kindFilter.value
    kindFilter.innerHTML = insuranceKindOptionsHtml(kinds)
    kindFilter.value = kinds.includes(selected) ? selected : ''
    kindFilter.disabled = false
    renderRows()
  }

  function isSameTabLuxLink(event) {
    const link = event.target.closest('a[href]')
    return Boolean(
      link?.href.startsWith(baseApiUrl) &&
      !link.target &&
      event.button === 0 &&
      !event.defaultPrevented &&
      !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
    )
  }

  function stopLeaving() {
    leaving = false
    clearTimeout(leavingTimer)
    leavingHost?.removeAttribute('data-insurance-navigating')
    leavingHost?.querySelector('.lux-ins-navigating')?.remove()
    leavingHost = null
  }

  // The Lux page can take a moment to load; Impower's unload cleanup must not blank section 8 meanwhile.
  // `host` is the mounted overview or, before it settles, the pending skeleton.
  function showLeaving(host = root) {
    if (!host) return
    leaving = true
    leavingHost = host
    ensureInsurancePendingStyles()
    if (!host.querySelector('.lux-ins-navigating')) {
      host.setAttribute('data-insurance-navigating', '')
      host.append(createInsuranceNavigatingOverlay())
    }
    clearTimeout(leavingTimer)
    // Cancelled navigation or a page restored from the back-forward cache.
    leavingTimer = setTimeout(stopLeaving, 15000)
    window.addEventListener(
      'pageshow',
      (event) => {
        if (event.persisted) stopLeaving()
      },
      { once: true }
    )
  }

  async function loadStatus() {
    const controller = new AbortController()
    refreshRequest = controller
    try {
      const data = await luxInsuranceRequest(apiUrl, {
        signal: controller.signal,
      })
      if (disposed || controller.signal.aborted || !isCurrentPage()) return
      applyData(data)
    } catch (_) {
      // The mounted overview retries and shows the error in place of the rows.
    } finally {
      if (refreshRequest === controller) refreshRequest = null
      if (!disposed && !controller.signal.aborted) settle()
    }
  }

  async function refresh() {
    if (
      disposed ||
      actions.isDeleting() ||
      !root?.isConnected ||
      !isCurrentPage()
    ) {
      return
    }
    refreshRequest?.abort()
    const controller = new AbortController()
    refreshRequest = controller
    const host = root.querySelector('[data-insurance-table]')
    const status = root.querySelector('[data-insurance-status]')
    status.textContent = ''
    host.innerHTML = renderInsuranceTable([], propertyHrId, buildings, {
      loading: true,
      buildingsLoaded,
    })
    host.setAttribute('aria-busy', 'true')
    try {
      const data = await luxInsuranceRequest(apiUrl, {
        signal: controller.signal,
      })
      if (
        disposed ||
        controller.signal.aborted ||
        !host.isConnected ||
        !isCurrentPage()
      ) {
        return
      }
      applyData(data)
      showRows()
      mount()
      status.textContent = ''
    } catch (error) {
      if (!disposed && !controller.signal.aborted && host.isConnected) {
        const message = `${error.message} Bitte die Objektseite erneut laden.`
        if (loaded) {
          renderRows()
          status.textContent = message
        } else {
          host.innerHTML = renderInsuranceTable([], propertyHrId, buildings, {
            error: message,
            buildingsLoaded,
          })
        }
      }
    } finally {
      if (refreshRequest === controller) host.removeAttribute('aria-busy')
    }
  }

  function mount() {
    if (disposed || !isCurrentPage() || !settled || !buildingsReady) return
    const content = ensureSection()
    if (!content || root?.isConnected) return
    ensureTechnicalAssetsTableStyles()
    root = document.createElement('section')
    root.id = 'insurancePoliciesLux'
    root.className = 'sub-section impowerx-insurance-lux-root'
    root.tabIndex = -1
    root.setAttribute('aria-label', 'Versicherungsübersicht')
    root.innerHTML = insuranceOverviewShellHtml(
      propertyHrId,
      buildings,
      buildingsLoaded,
      insuranceFilterKinds(allowedKinds, rows)
    )
    root.querySelector('[data-insurance-search]').oninput = renderRows
    root.querySelector('[data-insurance-building]').onchange = renderRows
    root.querySelector('[data-insurance-kind]').onchange = renderRows
    const sortBy = (header) => {
      sort = nextInsuranceSort(sort, header.dataset.insuranceSort)
      page = 1
      renderRows()
    }
    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      const header = event.target.closest('th[data-insurance-sort]')
      if (!header) return
      event.preventDefault()
      sortBy(header)
    })
    root.addEventListener('click', async (event) => {
      const header = event.target.closest('th[data-insurance-sort]')
      if (header) {
        sortBy(header)
        return
      }
      const pageButton = event.target.closest('[data-insurance-page]')
      if (pageButton && !pageButton.disabled) {
        page = Number(pageButton.dataset.insurancePage)
        renderRows()
        root
          .querySelector('[data-insurance-table] table')
          ?.scrollIntoView?.({ block: 'nearest' })
        return
      }
      if (isSameTabLuxLink(event)) {
        showLeaving()
        return
      }
      const row = event.target.closest('[data-insurance-id]')
      if (!row) return
      if (event.target.closest('[data-copy-insurance]')) {
        event.preventDefault()
        event.stopPropagation()
        const policy = rows.find(
          (item) => String(item.id) === row.dataset.insuranceId
        )
        await copyInsurancePolicyNumber(policy)
      } else if (event.target.closest('[data-insurance-status-toggle]')) {
        event.preventDefault()
        event.stopPropagation()
        const button = event.target.closest('button')
        const policy = rows.find(
          (item) => String(item.id) === row.dataset.insuranceId
        )
        if (policy) {
          actions.changeStatus(
            policy,
            button,
            button.dataset.insuranceStatusToggle
          )
        }
      } else if (event.target.closest('[data-delete-insurance]')) {
        event.preventDefault()
        event.stopPropagation()
        const policy = rows.find(
          (item) => String(item.id) === row.dataset.insuranceId
        )
        if (policy) {
          actions.confirmDelete(policy, event.target.closest('button'))
        }
      }
    })
    content.prepend(root)
    replaceNativeInsuranceUi(propertyHrId)
    clearPending()
    // The first load already fetched these rows; remounts and a failed first load refetch.
    if (loaded && !rendered) showRows()
    else void refresh()
    revealAfterReturn()
    return true
  }

  // Impower tracks the active sidebar entry against its own sections and never sees the hidden native one.
  let sectionCurrent = false
  let forceActiveUntil = 0
  let scrollFrame = 0
  function syncSidebarActive() {
    if (!section?.isConnected || !isNativeInsuranceReplaced()) return
    const row = document.querySelector(
      `.FormSidebar .Link.main-link[${LUX_NATIVE_INSURANCE_NAV_ATTR}] .link-row.level1`
    )
    if (!row) return
    if (sectionCurrent || Date.now() < forceActiveUntil) {
      document
        .querySelectorAll('.FormSidebar .link-row.level1.active')
        .forEach((other) => {
          if (other !== row) other.classList.remove('active')
        })
      if (!row.classList.contains('active')) row.classList.add('active')
    } else if (row.classList.contains('active')) {
      row.classList.remove('active')
    }
  }
  const onScroll = () => {
    if (scrollFrame) return
    scrollFrame = requestAnimationFrame(() => {
      scrollFrame = 0
      if (!section?.isConnected) return
      const rect = section.getBoundingClientRect()
      const line = window.innerHeight / 3
      sectionCurrent = rect.top <= line && rect.bottom > line
      syncSidebarActive()
    })
  }

  const observer = new MutationObserver(() => {
    syncSidebarActive()
    markPending()
    clearTimeout(timer)
    timer = setTimeout(mount, 50)
  })
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  })
  const onPageShow = (event) => {
    if (event.persisted && !mount()) void refresh()
  }
  const onFocus = () => {
    if (
      document.visibilityState === 'hidden' ||
      actions.isConfirming() ||
      blockedPopover.isOpen()
    ) {
      return
    }
    if (root?.isConnected) void refresh()
    else if (!loaded && !refreshRequest) void loadStatus()
  }
  // The native sidebar entry would scroll to the hidden native section.
  const onSidebarClick = (event) => {
    if (!section?.isConnected || !isNativeInsuranceReplaced()) return
    const mainLink = event.target.closest?.('.FormSidebar .Link.main-link')
    if (!mainLink) return
    if (!mainLink.hasAttribute(LUX_NATIVE_INSURANCE_NAV_ATTR)) {
      sectionCurrent = false
      forceActiveUntil = 0
      return
    }
    event.preventDefault()
    event.stopPropagation()
    // Keeps 8 active while the smooth scroll passes the sections above it.
    forceActiveUntil = Date.now() + 1000
    syncSidebarActive()
    section.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }
  window.addEventListener('pageshow', onPageShow)
  window.addEventListener('focus', onFocus)
  document.addEventListener('click', onSidebarClick, true)
  document.addEventListener('scroll', onScroll, {
    capture: true,
    passive: true,
  })
  const lifecycle = {
    disconnect() {
      if (disposed) return
      disposed = true
      observer.disconnect()
      clearTimeout(timer)
      refreshRequest?.abort()
      actions.abortDelete()
      window.removeEventListener('pageshow', onPageShow)
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('click', onSidebarClick, true)
      document.removeEventListener('scroll', onScroll, { capture: true })
      cancelAnimationFrame(scrollFrame)
      actions.abortConfirmation()
      blockedPopover.close()
      clearTimeout(pendingTimer)
      if (!leaving) {
        clearPending()
        root?.remove()
        removeSection()
        // A re-inject of the same page keeps its state so native insurance does not flash in between.
        if (!isCurrentPage()) releaseNativeInsuranceUi()
      }
      if (activeInsurancePropertyTable === lifecycle) {
        activeInsurancePropertyTable = null
      }
    },
  }
  activeInsurancePropertyTable = lifecycle
  holdNativeInsuranceUi(propertyHrId)
  markPending()
  void loadStatus()
  if (buildingsRequest) void loadBuildings()
  return lifecycle
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { injectInsurancePropertyTable }
}
